/**
 * The loading screen's pure logic (q-asset-readiness-loading-screen, AD-794), kept out of the
 * component so `node --test` covers it with no DOM: the honest progress fraction, the
 * player-facing "what are we waiting on" line, the cloud-parting amounts, and the lore order.
 */

import type { ReadinessProgress } from '../../game/readiness.js';

/** Auto-advance a lore card after this long (ms); a click / tap / key advances at once. */
export const LORE_ROTATE_MS = 9000;
/** The reveal: clouds finish parting and the screen fades over this long (ms). */
export const REVEAL_MS = 700;
/** The reveal under reduced motion: an immediate, near-instant fade (ms). */
export const REVEAL_REDUCED_MS = 120;

/** done / total, clamped to [0, 1]; 0 while nothing has been counted yet. */
export function progressFraction(progress: ReadinessProgress): number {
  if (progress.total <= 0) return 0;
  return Math.min(1, Math.max(0, progress.done / progress.total));
}

/** The plural noun for each kind of thing the screen can wait on, by its label prefix. */
const CATEGORY: Readonly<Record<string, readonly [singular: string, plural: string]>> = {
  creature: ['a creature', 'creatures'],
  player: ['a traveller', 'travellers'],
  npc: ['a townsperson', 'townsfolk'],
  'npc station': ['a workbench', 'workbenches'],
  node: ['a resource', 'resources'],
  equipment: ['your gear', 'gear'],
  prop: ['scenery', 'scenery'],
  dressing: ['scenery', 'scenery'],
  scatter: ['scenery', 'scenery'],
  marker: ['a landmark', 'landmarks'],
  'ground texture': ['the ground', 'the ground'],
};

/** Whole-label items (no `kind: name` form) and how they read. */
const WHOLE: Readonly<Record<string, string>> = {
  'the renderer': 'the renderer',
  'the world': 'the world',
  'the content pack': "the world's records",
  'your character': 'your character',
};

/**
 * One short, player-facing line from the session's `waitingOn` labels (`kind: name` or
 * `kind: name ×n`): "your character · creatures · scenery". Asset ids never reach the player;
 * the counts stay in the progress line. At most `max` parts.
 */
export function describeWaiting(labels: readonly string[], max = 3): string {
  const parts: string[] = [];
  const counts = new Map<string, number>();
  for (const raw of labels) {
    const whole = WHOLE[raw];
    if (whole !== undefined) {
      if (!parts.includes(whole)) parts.push(whole);
      continue;
    }
    const match = /^([^:]+):.*?(?:\s×(\d+))?$/.exec(raw);
    const kind = match?.[1]?.trim() ?? raw;
    counts.set(kind, (counts.get(kind) ?? 0) + Number(match?.[2] ?? 1));
  }
  for (const [kind, n] of counts) {
    const names = CATEGORY[kind];
    const word = names === undefined ? kind : n > 1 ? names[1] : names[0];
    if (!parts.includes(word)) parts.push(word);
  }
  return parts.slice(0, max).join(' · ');
}

/**
 * How far the cloud banks have parted, in [0, 1]: they track progress (an honest picture of
 * it) but never fully open before the reveal, so the world behind is never shown early.
 */
export function cloudParting(fraction: number, revealing: boolean): number {
  if (revealing) return 1;
  return 0.7 * Math.min(1, Math.max(0, fraction));
}

/** The next lore card index (wraps). */
export function nextLoreIndex(index: number, count: number): number {
  if (count <= 0) return 0;
  return (index + 1) % count;
}

/** Whether reduced motion is asked for: the OS setting or the in-game Accessibility toggle. */
export function prefersReducedMotion(
  matchMedia: ((query: string) => { matches: boolean }) | undefined,
  rootClasses: { contains(token: string): boolean } | undefined,
): boolean {
  if (rootClasses?.contains('pxl-reduce-motion') === true) return true;
  try {
    return matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  } catch {
    return false;
  }
}
